import type { Node } from 'tree-model'
import type { NavNode } from './navigation.config'
import type { RouteId } from '@core/routing/router.config'

// tree-model's Node<T> types `.model`/`.parent`/`.children` as `[propName: string]: any` —
// this is the single typed seam into that untyped surface.

export const getModel = (node: Node<NavNode>): NavNode => node.model

export const getParent = (node: Node<NavNode>): Node<NavNode> | undefined => node.parent

/**
 * The nearest ancestor that is actually a place you can go.
 *
 * Not every node in the tree is a route: a `group` node is a heading, and a
 * heading is not somewhere to navigate to. Anything that answers "one level up"
 * has to walk past those, or adding a heading around a link would silently
 * disable that link's Up button.
 */
export const getRoutableParent = (node: Node<NavNode>): Node<NavNode> | undefined => {
  let parent = getParent(node)
  while (parent && parent.model.routesTo === undefined) parent = getParent(parent)
  return parent
}

/**
 * `root` itself carries `routesTo: 'Home'`, deliberately: it is the only
 * routable ancestor a top-level workspace item (Assets, Teams, ...) has, so
 * `getRoutableParent` needs it there for the real "Up" button to reach Home.
 * `findByRouteId('Home')` is therefore ambiguous by construction — it also
 * matches 'Overview', the menu row for the same route — and the two matches
 * are not interchangeable: browsing that climbs past a top-level item wants
 * to reach the literal tree root as its ceiling (a dedicated, tested "show
 * the whole top-level menu" state, see `navigation.infra.test.ts` "leaves Up
 * and Section describing the route, not the browsed node"), which only this
 * unrestricted version can return. Resolving *the current URL's own menu
 * row* is a different question — see `findRouteNode` below.
 */
export const findByRouteId = (root: Node<NavNode>, routeId: RouteId | null): Node<NavNode> | undefined =>
  root.all((n) => n.model.routesTo === routeId)[0]

/**
 * Like `findByRouteId`, but excludes the tree's wrapper root — which is
 * never a rendered menu row — so it can't be mistaken for the node "current"
 * highlighting, breadcrumbs, and the sidebar's current-node label all mean
 * by "the route I'm actually on". Use this for anything describing the
 * current URL; use `findByRouteId` for anything describing where browsing
 * has climbed to.
 */
export const findRouteNode = (root: Node<NavNode>, routeId: RouteId | null): Node<NavNode> | undefined =>
  root.all((n) => n !== root && n.model.routesTo === routeId)[0]

const hasVisibleChild = (node: Node<NavNode>): boolean =>
  ((node.children ?? []) as Node<NavNode>[]).some((child) => child.model.visible !== false)

/**
 * The nearest ancestor that is both routable and has something to show — a
 * detail route's own parent (e.g. Assets) is routable but its only child is
 * an invisible route-resolution marker, so landing the browse anchor there
 * would show an empty menu and cost the user a second click for nothing.
 */
export const getBrowsableParent = (node: Node<NavNode>): Node<NavNode> | undefined => {
  let cursor = getRoutableParent(node)
  while (cursor && !hasVisibleChild(cursor)) cursor = getRoutableParent(cursor)
  return cursor
}

/**
 * Is `node` the route's own node, or an ancestor of it? "Which menu row am I
 * on" has to survive a detail route: the row for Assets still describes where
 * the user is while they are inside one of its assets.
 */
export const containsRoute = (node: Node<NavNode>, routeNode: Node<NavNode> | undefined): boolean => {
  let cursor = routeNode
  while (cursor) {
    if (cursor === node) return true
    cursor = getParent(cursor)
  }
  return false
}
